import React, { useEffect, useRef, useState } from 'react';
import { Skeleton } from '@tyndo/ui-kit';
import { LayoutGrid, AlertTriangle } from 'lucide-react';
import { widgetRegistry } from '../../registries/WidgetRegistry';
import type {
  LayoutWidgetInstance,
  WidgetDefinition,
} from '../LandingPageEditor/types';
import { useInView } from './useInView';

export interface WidgetTileProps {
  instance: LayoutWidgetInstance;
  definition?: WidgetDefinition;
  /** When true, render labelled chrome only — never a live widget / data preview. */
  editing?: boolean;
  /** Editing-mode overlay (jiggle badges, size control, etc.). */
  overlay?: React.ReactNode;
}

/**
 * One widget instance rendered inside a glass tile.
 *
 *  - Editing → glass chrome + muted icon + size label. NO live widget, so
 *    adding a widget shows a labelled placeholder rather than a data preview.
 *  - Otherwise → defer mounting (via `useInView`) until the tile scrolls into
 *    view, then lazily resolve the component from the runtime registry and
 *    render it. A bad widget is caught by an error boundary so one failure
 *    can't crash the page.
 */
const WidgetTile: React.FC<WidgetTileProps> = ({
  instance,
  definition,
  editing = false,
  overlay,
}) => {
  const title = definition?.label ?? instance.widgetId;

  return (
    <div className="relative h-full">
      {/* The edit-mode wobble is scoped to the CONTENT layer, never to the
          `relative` box above: that box is what the overlay's edit controls are
          positioned against, and an infinite rotation on it would keep their
          geometry moving forever — unclickable for anything (or anyone) that
          waits for a target to hold still. Rotating only the content keeps the
          affordance and leaves the controls put. */}
      <div
        className={
          editing
            ? 'h-full animate-[tyndo-jiggle_0.4s_ease-in-out_infinite] motion-reduce:animate-none'
            : 'h-full'
        }
      >
        {editing ? (
          <EditingChrome title={title} instance={instance} />
        ) : (
          <LiveTile instance={instance} title={title} />
        )}
      </div>
      {overlay}
    </div>
  );
};

// ── Editing placeholder — labelled chrome, never live data ─────────────────

const EditingChrome: React.FC<{
  title: string;
  instance: LayoutWidgetInstance;
}> = ({ title, instance }) => (
  <div className="glass rounded-2xl h-full min-h-[8rem] p-4 flex flex-col gap-3">
    <div className="flex items-start gap-2 min-w-0">
      <span
        className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary shrink-0"
        aria-hidden="true"
      >
        <LayoutGrid className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{title}</p>
        <p className="t-mono text-[10px] text-muted-foreground/70 truncate">
          {instance.widgetId}
        </p>
      </div>
    </div>
    <div className="mt-auto flex items-center justify-between text-[10px] text-muted-foreground">
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5">
        size <span className="t-mono">{instance.size}</span>
      </span>
      <span className="opacity-70">Preview hidden while editing</span>
    </div>
  </div>
);

// ── Live tile — deferred, lazily-resolved, error-isolated ──────────────────

const LiveTile: React.FC<{ instance: LayoutWidgetInstance; title: string }> = ({
  instance,
  title,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { rootMargin: '200px' });
  const [Component, setComponent] = useState<React.ComponentType | null>(null);
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'missing'>(
    'idle',
  );

  useEffect(() => {
    if (!inView || state !== 'idle') return;
    let alive = true;
    setState('loading');
    widgetRegistry
      .resolve(instance.widgetId)
      .then((resolved) => {
        if (!alive) return;
        if (resolved) {
          setComponent(() => resolved);
          setState('ready');
        } else {
          setState('missing');
        }
      })
      .catch(() => {
        if (alive) setState('missing');
      });
    return () => {
      alive = false;
    };
  }, [inView, instance.widgetId, state]);

  return (
    <div ref={ref} className="h-full">
      {state === 'ready' && Component ? (
        <WidgetErrorBoundary title={title}>
          <Component />
        </WidgetErrorBoundary>
      ) : state === 'missing' ? (
        <UnavailableTile title={title} widgetId={instance.widgetId} />
      ) : (
        <TileSkeleton title={title} />
      )}
    </div>
  );
};

const TileSkeleton: React.FC<{ title: string }> = ({ title }) => (
  <div className="glass rounded-2xl h-full min-h-[8rem] p-4 flex flex-col gap-3">
    <p className="text-sm font-medium text-foreground truncate">{title}</p>
    <Skeleton className="h-4 w-2/3" />
    <Skeleton className="h-4 w-1/2" />
    <Skeleton className="mt-auto h-8 w-full rounded-xl" />
  </div>
);

const UnavailableTile: React.FC<{ title: string; widgetId: string }> = ({
  title,
  widgetId,
}) => (
  <div className="glass rounded-2xl h-full min-h-[8rem] p-4 flex flex-col items-center justify-center gap-2 text-center">
    <AlertTriangle
      className="h-5 w-5 text-muted-foreground"
      aria-hidden="true"
    />
    <p className="text-sm font-medium text-foreground">{title}</p>
    <p className="text-xs text-muted-foreground">
      Widget unavailable on this device.
    </p>
    <p className="t-mono text-[10px] text-muted-foreground/60">{widgetId}</p>
  </div>
);

// ── Error boundary so one bad widget can't crash the whole canvas ──────────

class WidgetErrorBoundary extends React.Component<
  { title: string; children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { title: string; children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  override componentDidCatch(error: unknown) {
    // Keep the canvas alive; surface to the console for diagnostics.
    console.error('Widget render failed', this.props.title, error);
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="glass rounded-2xl h-full min-h-[8rem] p-4 flex flex-col items-center justify-center gap-2 text-center">
          <AlertTriangle
            className="h-5 w-5 text-alarm-warning"
            aria-hidden="true"
          />
          <p className="text-sm font-medium text-foreground">
            {this.props.title}
          </p>
          <p className="text-xs text-muted-foreground">
            This widget failed to render.
          </p>
        </div>
      );
    }
    return this.props.children;
  }
}

export default WidgetTile;
